import React, { useState, useEffect } from "react";
import { NavLink, Routes, Route } from "react-router-dom";
import {
  Users, User, Package,
  Layers, FileText,
  Menu, LogOut, ChevronDown, LayoutDashboard,
  BookOpen, MapPin, Handshake, Grid3x3, Megaphone,
  TrendingUp, RotateCcw, ShoppingCart, ShoppingBag,
  ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine,
  Wallet, Receipt, Database, Settings, Bell, X,
  PhoneCall, Wrench, UserCog, FileBarChart, UserCheck,
} from "lucide-react";
import "../../styles/dashboard.css";
import { useNavigate } from "react-router-dom";

import Clients from "../internals/Clients";
import UserScreen from "../internals/UserScreen";
import Partners from "../internals/Partners";
import Area from "../internals/Area";
import ProductGroup from "../internals/ProductGroup";
import Complaint from "../internals/Complaint";
import Overview from "../internals/OverView";
import CrmLeads from "../internals/CrmLeads";
import ServiceCalls from "../internals/ServiceCalls";
import Executives from "../internals/Executives";
import Reports from "../internals/Reports";
import Owners from "../internals/Owners";

const menuGroups = [
  {
    group: "Dashboard",
    icon: LayoutDashboard,
    path: "overview",
    single: true,
  },
  {
    group: "Sales CRM",
    icon: PhoneCall,
    children: [
      { path: "crm-leads", label: "Leads / Pipeline", icon: PhoneCall },
      { path: "service-calls", label: "Service Calls", icon: Wrench },
    ],
  },
  {
    group: "Admin",
    icon: Users,
    children: [
      { path: "clients", label: "Clients", icon: Users },
      { path: "owners", label: "Owners", icon: UserCheck },
      { path: "executives", label: "Executives", icon: UserCog },
      { path: "users", label: "Users", icon: User },
      { path: "partner", label: "Partners", icon: Handshake },
      { path: "complaint", label: "Complaint", icon: Megaphone },
      { path: "reports", label: "Reports", icon: FileBarChart },
    ],
  },
  {
    group: "Masters",
    icon: Database,
    children: [
      { path: "product", label: "Products", icon: Package },
      { path: "account", label: "Chart of A/c", icon: BookOpen },
      {
        label: "Sub Masters",
        icon: Grid3x3,
        isSubGroup: true,
        children: [
          { path: "group", label: "Group Master", icon: Layers },
          { path: "area", label: "Area Master", icon: MapPin },
        ],
      },
    ],
  },
  {
    group: "Transactions",
    icon: FileText,
    children: [
      {
        label: "Inventory",
        icon: Package,
        isSubGroup: true,
        children: [
          { path: "sales", label: "Sales", icon: TrendingUp },
          { path: "sales-return", label: "Sales Return", icon: RotateCcw },
          { path: "purchase", label: "Purchase", icon: ShoppingCart },
          { path: "purchase-return", label: "Purchase Return", icon: ShoppingBag },
          {
            label: "Stock Transfer",
            icon: ArrowLeftRight,
            isSubGroup: true,
            children: [
              { path: "stock-inwards", label: "Inwards", icon: ArrowDownToLine },
              { path: "stock-outwards", label: "Outwards", icon: ArrowUpFromLine },
            ],
          },
        ],
      },
      {
        label: "Accounts",
        icon: Wallet,
        isSubGroup: true,
        children: [
          { path: "receipts", label: "Receipts", icon: Receipt },
          { path: "payments", label: "Payments", icon: Wallet },
        ],
      },
    ],
  },
];

const isAnyChildActive = (children = []) =>
  children.some((c) =>
    c.children
      ? isAnyChildActive(c.children)
      : window.location.pathname.includes(`/${c.path}`)
  );

const SubGroup = ({ item, depth, onNavClick }) => {
  const anyActive = isAnyChildActive(item.children);
  const [open, setOpen] = useState(anyActive);

  return (
    <li className="sb-subgroup">
      <button
        className={`sb-subgroup-btn ${anyActive ? "active" : ""}`}
        style={{ paddingLeft: `${12 + depth * 8}px` }}
        onClick={() => setOpen((o) => !o)}
      >
        <item.icon size={13} className="sb-icon" />
        <span>{item.label}</span>
        <ChevronDown
          size={12}
          className={`sb-chevron ${open || anyActive ? "rotated" : ""}`}
        />
      </button>

      {(open || anyActive) && (
        <ul className="sb-submenu">
          {item.children.map((child, i) =>
            child.isSubGroup ? (
              <SubGroup key={i} item={child} depth={depth + 1} onNavClick={onNavClick} />
            ) : (
              <li key={child.path}>
                <NavLink
                  to={`/dashboard/${child.path}`}
                  className={({ isActive }) =>
                    `sb-sublink ${isActive ? "active" : ""}`
                  }
                  style={{ paddingLeft: `${20 + depth * 8}px` }}
                  onClick={onNavClick}
                >
                  <child.icon size={13} className="sb-icon" />
                  <span>{child.label}</span>
                </NavLink>
              </li>
            )
          )}
        </ul>
      )}
    </li>
  );
};

const MenuItem = ({ item, collapsed, onNavClick }) => {
  const anyActive = !item.single && isAnyChildActive(item.children);
  const [open, setOpen] = useState(anyActive);

  if (item.single) {
    return (
      <li>
        <NavLink
          to={`/dashboard/${item.path}`}
          className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
          title={collapsed ? item.group : ""}
          onClick={onNavClick}
        >
          <item.icon size={18} className="sb-icon" />
          {!collapsed && <span className="sb-label">{item.group}</span>}
        </NavLink>
      </li>
    );
  }

  return (
    <li className={`sb-group ${open || anyActive ? "open" : ""}`}>
      <button
        className={`sb-group-btn ${anyActive ? "active" : ""}`}
        onClick={() => !collapsed && setOpen((o) => !o)}
        title={collapsed ? item.group : ""}
      >
        <item.icon size={18} className="sb-icon" />
        {!collapsed && (
          <>
            <span className="sb-label">{item.group}</span>
            <ChevronDown
              size={14}
              className={`sb-chevron ${open || anyActive ? "rotated" : ""}`}
            />
          </>
        )}
      </button>

      {!collapsed && (open || anyActive) && (
        <ul className="sb-submenu">
          {item.children.map((child, i) =>
            child.isSubGroup ? (
              <SubGroup key={i} item={child} depth={1} onNavClick={onNavClick} />
            ) : (
              <li key={child.path}>
                <NavLink
                  to={`/dashboard/${child.path}`}
                  className={({ isActive }) =>
                    `sb-sublink ${isActive ? "active" : ""}`
                  }
                  onClick={onNavClick}
                >
                  <child.icon size={14} className="sb-icon" />
                  <span>{child.label}</span>
                </NavLink>
              </li>
            )
          )}
        </ul>
      )}

      {collapsed && (
        <div className="sb-tooltip">
          <div className="sb-tooltip-title">{item.group}</div>
          {item.children.map((child, i) =>
            child.isSubGroup ? (
              <div key={i}>
                <div className="sb-tooltip-section">{child.label}</div>
                {child.children?.map((c) =>
                  c.isSubGroup ? (
                    <div key={c.label}>
                      <div className="sb-tooltip-section" style={{ paddingLeft: 16 }}>{c.label}</div>
                      {c.children?.map((cc) => (
                        <NavLink key={cc.path} to={`/dashboard/${cc.path}`} className="sb-tooltip-link" style={{ paddingLeft: 24 }}>
                          {cc.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : (
                    <NavLink key={c.path} to={`/dashboard/${c.path}`} className="sb-tooltip-link" style={{ paddingLeft: 16 }}>
                      {c.label}
                    </NavLink>
                  )
                )}
              </div>
            ) : (
              <NavLink key={child.path} to={`/dashboard/${child.path}`} className="sb-tooltip-link">
                {child.label}
              </NavLink>
            )
          )}
        </div>
      )}
    </li>
  );
};

// ── Profile Dropdown ─────────────────────────────────
const ProfileDropdown = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, []);

  return (
    <div className="profile-wrapper" ref={ref}>
      {open && (
        <div
          className="profile-backdrop"
          onClick={() => setOpen(false)}
        />
      )}

      <button
        className={`profile-trigger ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open profile menu"
        aria-expanded={open}
      >
        <div className="avatar">A</div>
        <span className="username">Admin</span>
        <ChevronDown
          size={14}
          className={`profile-chevron ${open ? "rotated" : ""}`}
        />
      </button>

      {open && (
        <div className="profile-dropdown">
          <div className="profile-dropdown-header">
            <div className="profile-dropdown-avatar">A</div>
            <div className="profile-dropdown-info">
              <span className="profile-dropdown-name">Admin User</span>
              <span className="profile-dropdown-email">admin@xqlusive.com</span>
            </div>
          </div>

          <div className="profile-dropdown-divider" />

          <NavLink
            to="/dashboard/users"
            className="profile-dropdown-item"
            onClick={() => setOpen(false)}
          >
            <User size={15} />
            <span>My Profile</span>
          </NavLink>

          <button className="profile-dropdown-item">
            <Settings size={15} />
            <span>Settings</span>
          </button>

          <button className="profile-dropdown-item">
            <Bell size={15} />
            <span>Notifications</span>
          </button>

          <div className="profile-dropdown-divider" />

          <button
            className="profile-dropdown-item profile-dropdown-logout"
            onClick={() => navigate("/")}
          >
            <LogOut size={15} />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ── Dashboard Layout ─────────────────────────────────
const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = () => window.innerWidth <= 768;

  // Close sidebar on route change (mobile)
  const handleNavClick = () => {
    if (isMobile()) setMobileOpen(false);
  };

  // Close on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when mobile sidebar open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="dashboard">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <span className="brand-x">XQ</span>
            <span className="brand-rest">lusive</span>
          </div>
          {/* Desktop collapse btn */}
          <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
            <Menu size={19} />
          </button>
          {/* Mobile close btn */}
          <button
            className="collapse-btn"
            style={{ display: mobileOpen ? "flex" : "none" }}
            onClick={() => setMobileOpen(false)}
          >
            <X size={19} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {menuGroups.map((item) => (
              <MenuItem
                key={item.group}
                item={item}
                collapsed={collapsed && !mobileOpen}
                onNavClick={handleNavClick}
              />
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          {(!collapsed || mobileOpen) && <span className="sb-version">XQlusive v1.0</span>}
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <div className="topbar-left">
            {/* Mobile hamburger */}
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <span className="page-title">Dashboard</span>
          </div>
          <ProfileDropdown />
        </header>

        <main className="dashboard-content">
          <Routes>
            <Route path="overview" element={<Overview />} />
            <Route path="crm-leads" element={<CrmLeads />} />
            <Route path="service-calls" element={<ServiceCalls />} />
            <Route path="clients" element={<Clients />} />
            <Route path="executives" element={<Executives />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<UserScreen />} />
            <Route path="party" element={<h2>Party</h2>} />
            <Route path="product" element={<h2>Product</h2>} />
            <Route path="company" element={<h2>Company</h2>} />
            <Route path="van" element={<h2>Van</h2>} />
            <Route path="route" element={<h2>Route</h2>} />
            <Route path="account" element={<h2>Account Master</h2>} />
            <Route path="group" element={<ProductGroup />} />
            <Route path="area" element={<Area />} />
            <Route path="partner" element={<Partners />} />
            <Route path="owners" element={<Owners />} />
            <Route path="complaint" element={<Complaint />} />
            <Route path="sales" element={<h2>Sales</h2>} />
            <Route path="sales-return" element={<h2>Sales Return</h2>} />
            <Route path="purchase" element={<h2>Purchase</h2>} />
            <Route path="purchase-return" element={<h2>Purchase Return</h2>} />
            <Route path="stock-inwards" element={<h2>Stock Inwards</h2>} />
            <Route path="stock-outwards" element={<h2>Stock Outwards</h2>} />
            <Route path="receipts" element={<h2>Receipts</h2>} />
            <Route path="payments" element={<h2>Payments</h2>} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;